/**
 * Type-safe translation system for SmartDry POS
 * 
 * This file now uses a modular structure:
 * - All translations are loaded from JSON namespace files (locales/{lang}/*.json)
 * - Translations are organized by namespace for better maintainability
 * 
 * The modular structure provides:
 * - Better organization (separate files per namespace)
 * - Easier collaboration (translators can work on specific namespaces)
 * - Type safety maintained through the loader
 * 
 * Structure:
 * - frontend/src/i18n/locales/{en,es,fr,ar}/{common,pos,cart,items,customers,discounts,modals,till,messages}.json
 */

// Import all translations from modular loader (JSON files)
import { translations as loaderTranslations, type TranslationKey, type SupportedLanguage } from './translations-loader';

// ============================================================================
// All languages loaded from JSON files via translations-loader
// ============================================================================
const en = loaderTranslations.en;
const es = loaderTranslations.es;
const fr = loaderTranslations.fr;
const ar = loaderTranslations.ar;

// ============================================================================
// EXPORTS & TYPE GENERATION
// ============================================================================

/**
 * All translations organized by language code
 * All languages now come from modular JSON files
 */
export const translations = {
  en,
  es,
  fr,
  ar,
} as const;

// Re-export types from loader
export type { SupportedLanguage, TranslationKey } from './translations-loader';

/**
 * Get a value from a nested object using a dot-notation path
 */
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
}

/**
 * Type-safe translation function
 * @param key - Translation key (e.g., 'cart.checkout')
 * @param params - Optional parameters for interpolation (e.g., {count: 5})
 * @param language - Language code (defaults to current language)
 */
export function translate(
  key: TranslationKey,
  params?: Record<string, string | number>,
  language: SupportedLanguage = 'en'
): string {
  let translation = getNestedValue(translations[language], key);

  // Fallback to English if not found
  if (typeof translation !== 'string') {
    translation = getNestedValue(translations.en, key);
  }

  // Fallback to key itself if still not found
  if (typeof translation !== 'string') {
    translation = key;
  }

  // Handle interpolation (e.g., "{{count}} items")
  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translation = translation.replace(`{{${paramKey}}}`, String(paramValue));
    });
  }

  return translation;
}

/**
 * Export default for convenience
 */
export default translations;
